import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { IoClose } from "react-icons/io5";

import { MonetaLogo } from "@/components/atoms/MonetaLogo";
import { useScrollLock, useViewportHeight } from "@/hooks";
import useFlag from "@/hooks/useFlag";
import FeatureFlags from "@/utils/featureFlags";

import * as s from "./styles";

type NavigationLink = {
  path: string;
  label: React.ReactNode | string;
  key: string;
  altLabel?: React.ReactNode | string;
};

const getNavigationLinkLabel = (link: NavigationLink, showNewActivityLibrary: boolean) => {
  if (showNewActivityLibrary && link.altLabel) {
    return link.altLabel;
  }

  return link.label;
};

export const Navigation = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const { boxStyle: viewportHeight } = useViewportHeight();
  const showNewActivityLibrary = useFlag(FeatureFlags.SHOW_NEW_ACTIVITY_LIBRARY);

  let links: NavigationLink[];
  if (showNewActivityLibrary) {
    links = [...baseNavLinks, voiceMessagesLibraryLink];
  } else {
    links = baseNavLinks;
  }

  useScrollLock(isMenuExpanded);

  const handleMenuClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    setIsMenuExpanded(!isMenuExpanded);
  };

  return (
    <s.Container>
      <s.FullWidthNav>
        <s.LogoWrapper to="/">
          <MonetaLogo height={64} />
        </s.LogoWrapper>

        {links.map((link) => (
          <s.Link to={link.path} key={link.key}>
            {getNavigationLinkLabel(link, showNewActivityLibrary)}
          </s.Link>
        ))}
        <s.RightContainer>
          <s.LogoutLink to="/logout">Logout</s.LogoutLink>
          <s.LogoutLink to="/settings">Settings</s.LogoutLink>
        </s.RightContainer>
      </s.FullWidthNav>

      <s.ResponsiveNav>
        <s.MenuButton onClick={handleMenuClick}>
          <s.MenuIcon /> Menu
        </s.MenuButton>

        <AnimatePresence initial={false}>
          {isMenuExpanded && (
            <s.ResponsiveMenu
              initial="hidden"
              animate="visible"
              exit="hiden"
              variants={listAnimationProps}
            >
              <s.CloseMenuButton onClick={handleMenuClick}>
                <IoClose />
              </s.CloseMenuButton>

              <s.MenuItems style={viewportHeight}>
                {links.map((link, index) => (
                  <s.MenuItem variants={itemAnimationProps} key={index}>
                    <s.MenuLink to={link?.path}>{link?.label}</s.MenuLink>
                  </s.MenuItem>
                ))}

                <s.ChangePasswordWrapper variants={itemAnimationProps}>
                  <s.LogoutNavLink to="/logout">Logout</s.LogoutNavLink>
                </s.ChangePasswordWrapper>
                <s.LogoutButtonWrapper variants={itemAnimationProps}>
                  <s.LogoutNavLink to="/settings">Settings</s.LogoutNavLink>
                </s.LogoutButtonWrapper>
              </s.MenuItems>
            </s.ResponsiveMenu>
          )}
        </AnimatePresence>
      </s.ResponsiveNav>
    </s.Container>
  );
};

const listAnimationProps = {
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      when: "afterChildren",
    },
  },
};

const itemAnimationProps = {
  visible: {
    opacity: 1,
    y: 0,
  },
  hidden: {
    opacity: 0,
    y: 40,
    transition: {
      when: "afterChildren",
    },
  },
};

const baseNavLinks: NavigationLink[] = [
  { key: "NAV_LINKS_PATIENTS_1", path: "/patients", label: "Patients" },
  {
    key: "NAV_LINKS_ACTIVITIES_2",
    path: "/activities",
    label: "Activity Library",
    altLabel: "Activities",
  },
];

const voiceMessagesLibraryLink: NavigationLink = {
  key: "NAV_LINKS_VOICE_MESSAGES_3",
  path: "/voice-messages",
  label: "Voice Messages Library",
  altLabel: "Voice Messages",
};
